import React, { FunctionComponent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { generateUrlWithShareParameter } from '../../utils/generateUrlWithShareParameter';
import { ShareQueryParameter } from '../../constants/social';
import useBasePageQuery from '../../hooks/useBasePageQuery';

const CampaignsHelmet: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const basePageData = useBasePageQuery();

  const {
    currentUrl,
    metaTagImageUrl,
    metaTagDescription,
  } = useNotNullableContext(PageMetaContext);

  if (!basePageData.data?.page) {
    return null;
  }

  const { hero, startFundraisingUrl, summary, title } = basePageData.data.page;

  const imageUrl =
    metaTagImageUrl ||
    hero?.imageUrl ||
    makePhotoUrl('trainer-no-branding_web.jpg', 'CampaignHeroMedium');
  const metaTagValues: ShareQueryParameter = {
    ogImageUrl: metaTagImageUrl,
    ogDescription: metaTagDescription,
  };
  const canonicalUrl = generateUrlWithShareParameter(
    currentUrl,
    metaTagValues,
    false,
  );

  return (
    <Helmet
      title={`${title} - JustGiving`}
      meta={[
        { property: 'og:title', content: title },
        {
          property: 'og:description',
          content: metaTagDescription || summary || '',
        },
        {
          property: 'og:image',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          property: 'og:image:secure_url',
          content: `${imageUrl}?template=size1200x630face`,
        },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:url', content: canonicalUrl },
        { name: 'twitter:card', content: 'summary_large_image' },
        {
          name: 'twitter:image:src',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          name: 'twitter:description',
          content: metaTagDescription,
        },
        {
          name: 'twitter:domain',
          content: 'justgiving.com',
        },
        { property: 'og:site_name', content: 'Just Giving' },
        {
          name: 'jg:chrome:featuredItemLabel',
          content: `${t('campaign.startFundraisingCTA')}`,
        },
        {
          name: 'jg:chrome:featuredItemUrl',
          content: startFundraisingUrl!,
        },
      ]}
    />
  );
};

export default CampaignsHelmet;
